import {
  format,
  formatDistanceToNow,
  isValid,
  parseISO,
  type Locale,
} from "date-fns";
import moment from "moment";
import { serverNow } from "./serverClock";

import { ROUTES } from "@/constants/routes";
import type { TGetCheckedHistory } from "@/types/(my-space)/timeAttendace.interface";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { PLATFORM_NAME } from "../constants/env";

dayjs.extend(utc);

export const formatTimeAgo = (date: string | Date): string => {
  if (!date) return "N/A";
  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (!isValid(dateObj)) return "Invalid date";
  return formatDistanceToNow(dateObj, { addSuffix: false });
};

/**
 * Directional, glanceable time: "just now", "3 days ago", "in 4 days".
 *
 * `formatTimeAgo` above drops the suffix, so it cannot say which side of now a
 * date falls on — fine for a feed where everything is past, useless for "back
 * Monday" or "waiting 3 days", where the direction is the whole point.
 *
 * Sub-minute collapses to "just now" rather than "less than a minute ago": on a
 * tile the reader is glancing at, the distinction between 20 and 50 seconds is
 * noise, and the shorter string is far less likely to wrap.
 *
 * Past a fortnight it switches to an absolute date — "94 days ago" tells nobody
 * anything, and an activity feed reading it needs the real day.
 */
export const formatRelativeTime = (date: string | Date | null): string => {
  if (!date) return "N/A";
  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (!isValid(dateObj)) return "—";

  const diffMs = dateObj.getTime() - Date.now();
  if (Math.abs(diffMs) < 60_000) return "just now";
  if (Math.abs(diffMs) > 14 * 86_400_000) return format(dateObj, "MMM dd, yyyy");
  return formatDistanceToNow(dateObj, { addSuffix: true });
};

// `t` is optional so this stays callable from non-component contexts (it
// isn't currently, but keeping the signature backward-compatible avoids
// forcing every caller to change at once); pass it wherever one is in scope.
export const formatRegularizedTime = (
  regularizedHours: number,
  t?: (key: string, options?: Record<string, unknown>) => string,
): string => {
  const hours = Math.floor(regularizedHours);
  let minutes = Math.round((regularizedHours - hours) * 60);

  if (minutes === 60) {
    minutes = 0;
    return t
      ? t("common:shared_ui.duration_hrs_min", { hours: hours + 1, minutes })
      : `${hours + 1} Hrs ${minutes} Min`;
  }
  return t
    ? t("common:shared_ui.duration_hrs_min", { hours, minutes })
    : `${hours} Hrs ${minutes} Min`;
};

export const formatDateTime = (date: string | Date | null): string => {
  if (!date) return "N/A";
  return format(new Date(date), "MMM dd, yyyy h:mm a");
};

/**
 * "Today · 14:30" / "Tomorrow · 09:00" / "Aug 15 · 14:30" — for scheduled things,
 * where the day matters more than the year and the clock time always matters.
 */
export const formatDayTime = (date: string | Date | null): string => {
  if (!date) return "N/A";
  const when = new Date(date);
  if (isNaN(when.getTime())) return "—";

  const time = format(when, "HH:mm");
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(when) - startOfDay(new Date())) / 86_400_000);

  if (dayDiff === 0) return `Today · ${time}`;
  if (dayDiff === 1) return `Tomorrow · ${time}`;
  if (dayDiff === -1) return `Yesterday · ${time}`;
  return `${format(when, "MMM dd")} · ${time}`;
};

/**
 * "AED 27,000" from the API's `"27000.00"`. Pass a null currency to get the
 * number alone (e.g. the upper end of a band that already shows the currency).
 */
export const formatMoney = (
  currency: string | null | undefined,
  value: string | number | null | undefined,
): string => {
  if (value === null || value === undefined || value === "") return "—";
  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  const rounded = amount.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return currency ? `${currency} ${rounded}` : rounded;
};

/** Two letters from a person's name, for the avatar tiles used across the app. */
export const initialsOf = (name: string): string =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

export const formatLocalTime = (date: string | Date | null): string => {
  if (!date) return "-";
  return dayjs(date).local().format("hh:mm A");
};

export const stripTrailingSlash = (p: string) =>
  p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p;

export const isActive = (pathname: string, itemPath: string) => {
  if (!pathname || !itemPath) return false;
  const normPathname = stripTrailingSlash(pathname);
  const normItemPath = stripTrailingSlash(itemPath);
  if (normPathname === normItemPath) {
    return true;
  }

  // If not exact, check for partial match for nested routes
  // Ensure that /admin/employees/add matches /admin/employees
  // but not /admin/employee-details
  if (
    normPathname.startsWith(normItemPath) &&
    normItemPath !== stripTrailingSlash(ROUTES.HOME.ROOT) &&
    normItemPath !== stripTrailingSlash(ROUTES.HOME.COMPANY.DASHBOARD)
  ) {
    const remainingPath = normPathname.substring(normItemPath.length);
    return remainingPath === "" || remainingPath.startsWith("/");
  }
  return false;
};

export const isWeekend = (date: string): boolean => {
  const day = new Date(date).getDay();
  return day === 0 || day === 6;
};

export const getStartAndEndOfWeek = () => {
  const today = new Date();
  const startOfWeek = new Date(today);
  const dayOfWeek = today.getDay();
  const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  startOfWeek.setDate(today.getDate() - diff);

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);

  return {
    start: format(startOfWeek, "yyyy-MM-dd"),
    end: format(endOfWeek, "yyyy-MM-dd"),
  };
};

/**
 * `dd MMM yyyy` for table cells.
 *
 * date-fns, not `toLocaleDateString("en-GB", …)`: the pinned locale rendered
 * English month names ("Sep") inside Arabic tables. `applyDocumentLanguage`
 * sets the date-fns default locale, so this follows the active language — the
 * same way the `formatDate` helper right below already does.
 */
export const formatDateTable = (dateString: string) =>
  format(new Date(dateString), "dd MMM yyyy");

export const formatDate = (date: string | Date | null): string => {
  if (!date) return "N/A";
  return format(new Date(date), "MMM dd, yyyy");
};

/** Numeric day-first with slashes: "dd/MM/yyyy". Compact list/timestamp use. */
export const formatDateSlash = (date: string | Date | null): string => {
  if (!date) return "N/A";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "—";
  return format(d, "dd/MM/yyyy");
};

export const formatTime = (date: string | Date | null): string => {
  if (!date) return "N/A";
  return format(new Date(date), "h:mm a");
};

/** 24-hour format: "HH:mm". Use for overtime/scheduling time ranges. */
export const formatTime24h = (date: string | Date | null): string => {
  if (!date) return "-";
  return format(new Date(date), "HH:mm");
};

/** Day-first compact: "dd MMM yyyy". Used in payrolls, documents, teams. */
export const formatDateDmy = (date: string | Date | null): string => {
  if (!date) return "N/A";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "—";
  return format(d, "dd MMM yyyy");
};

/** Day-first with comma: "dd MMM, yyyy". Used in projects, events. */
export const formatDateDmyComma = (date: string | Date | null): string => {
  if (!date) return "N/A";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "—";
  return format(d, "dd MMM, yyyy");
};

/** Day-first with time: "dd MMM yyyy, HH:mm". Used in payment files. */
export const formatDateTimeDmy = (date: string | Date | null): string => {
  if (!date) return "N/A";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "—";
  return format(d, "dd MMM yyyy, HH:mm");
};

export const formatTimeFromISO = (isoString = "", useUTC = false) => {
  if (!isoString) return ""; // handle empty input

  const date = new Date(isoString);
  if (isNaN(date.getTime())) return ""; // handle invalid date

  const hours24 = useUTC ? date.getUTCHours() : date.getHours();
  const minutes = useUTC ? date.getUTCMinutes() : date.getMinutes();

  const ampm = hours24 >= 12 ? "PM" : "AM";
  const hours12 = hours24 % 12 || 12; // convert 0 -> 12
  return `${String(hours12).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${ampm}`;
};

export const addMillisecondsToTime = (
  prevTime: string,
  msToAdd: number,
): string => {
  return moment(prevTime).add(msToAdd, "milliseconds").toISOString();
};

export const getTimeDifferenceInMS = (start: string, end: string | null) => {
  if (end) return moment(end).diff(moment(start));
};

export const getCurrentDateYMDformat = (dateString?: string) => {
  if (dateString) {
    if (
      !dateString.match(
        /^[0-9]{4}-(0?[1-9]|1[0-2])-(0?[1-9]|[12][0-9]|3[01])$/i,
      )
    ) {
      return moment(serverNow()).format("YYYY-MM-DD");
    }

    // Normalize the date string to ensure zero-padding (YYYY-MM-DD)
    const [year, month, day] = dateString.split("-").map(Number);
    const normalizedDateString = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

    return moment(normalizedDateString).format("YYYY-MM-DD");
  }

  return moment(serverNow()).format("YYYY-MM-DD");
};

export const getTimeDifference = (
  startTime: string,
  endTime?: string,
): string => {
  const start = moment(startTime);
  const end = endTime ? moment(endTime) : moment(serverNow());
  return start.from(end);
};

// `dateFnsLocale` is optional so this stays callable without one (falls back
// to the previous hardcoded-English behavior); pass `useLocale().dateFnsLocale`
// wherever one is in scope so the month abbreviation follows the app language.
export const formatDateMonthYear = (
  date: Date,
  dateFnsLocale?: Locale,
): string => format(date, "dd-MMM-yyyy", { locale: dateFnsLocale });

// Helper function to format date as YYYY-MM-DD
export const formatDateForParam = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

type TimePickerTime = {
  hour: number;
  minute: number;
  ampm: "AM" | "PM";
};

export const formatPickerTime = (time: TimePickerTime): string => {
  // eslint-disable-next-line prefer-const
  let { hour, minute, ampm } = time;

  if (ampm === "PM" && hour !== 12) {
    hour += 12;
  }
  if (ampm === "AM" && hour === 12) {
    hour = 0;
  }

  const hh = String(hour).padStart(2, "0");
  const mm = String(minute).padStart(2, "0");

  return `${hh}:${mm}`;
};

export const getTimeFromTimestamp = (timestamp?: string) => {
  if (!timestamp) {
    return "";
  }

  // Parse the timestamp and format it to extract the time in 12-hour format
  return moment(timestamp).format("hh:mm A"); // e.g., "12:41 PM"
};

/**
 * Normalizes a raw status string to a chip-safe value.
 * Lowercases and maps `auto_approved` → `approved`.
 * Safe to call on null/undefined — returns `""`.
 */
export const normalizeStatus = (status: string | null | undefined): string => {
  if (!status) return "";
  const lower = status.toLowerCase();
  return lower === "auto_approved" ? "approved" : lower;
};

/**
 * Formats `{ firstName, lastName }` to a display name string.
 * Returns `fallback` (default `"—"`) when the person is null/undefined or has no name.
 */
export const formatPerson = (
  person:
    | { firstName?: string | null; lastName?: string | null }
    | null
    | undefined,
  fallback = "—",
): string => {
  if (!person) return fallback;
  const name = `${person.firstName ?? ""} ${person.lastName ?? ""}`.trim();
  return name || fallback;
};

export const getTotalElapsedTime = (
  sessions: TGetCheckedHistory[],
  currentTime: number,
) => {
  let total = 0;
  let hasActiveSession = false;
  sessions.forEach((session) => {
    if (session.clockInTime && session.clockOutTime) {
      // Completed session
      const diff =
        new Date(session.clockOutTime).getTime() -
        new Date(session.clockInTime).getTime();
      if (!isNaN(diff) && diff > 0) {
        total += diff;
      }
    } else if (
      session.clockInTime &&
      !session.clockOutTime
      // &&
      // idx === sessions.length - 1
    ) {
      // Ongoing session (last one)
      const clockInTime = new Date(session.clockInTime).getTime();
      if (!isNaN(clockInTime)) {
        const diff = currentTime - clockInTime;
        // Only add positive differences to avoid negative time
        if (diff > 0) {
          total += diff;
        }
        hasActiveSession = true;
      }
    }
  });
  total = Math.max(0, total);

  const totalSeconds = Math.floor(total / 1000); // Use the current elapsed time
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  // Calculate overtime (standard work day is 8 hours = 28800 seconds)
  const standardWorkDaySeconds = 8 * 3600; // 8 hours in seconds
  const isOvertime = totalSeconds > standardWorkDaySeconds;
  const overtimeSeconds = isOvertime
    ? totalSeconds - standardWorkDaySeconds
    : 0;
  const regularTimeSeconds = isOvertime ? standardWorkDaySeconds : totalSeconds;

  // Format overtime
  const overtimeHours = Math.floor(overtimeSeconds / 3600);
  const overtimeMinutes = Math.floor((overtimeSeconds % 3600) / 60);
  const overtimeSecondsRemainder = overtimeSeconds % 60;

  // Format regular time
  const regularTimeHours = Math.floor(regularTimeSeconds / 3600);
  const regularTimeMinutes = Math.floor((regularTimeSeconds % 3600) / 60);
  const regularTimeSecondsRemainder = regularTimeSeconds % 60;

  return {
    hours,
    minutes,
    seconds,
    totalSeconds,
    hasActiveSession,
    totalPadString: `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`,
    // Overtime data
    isOvertime,
    overtimeSeconds,
    overtime: {
      hours: overtimeHours,
      minutes: overtimeMinutes,
      seconds: overtimeSecondsRemainder,
      totalSeconds: overtimeSeconds,
      padString: `${overtimeHours.toString().padStart(2, "0")}:${overtimeMinutes.toString().padStart(2, "0")}`,
    },
    // Regular time data
    regularTime: {
      hours: regularTimeHours,
      minutes: regularTimeMinutes,
      seconds: regularTimeSecondsRemainder,
      totalSeconds: regularTimeSeconds,
      padString: `${regularTimeHours.toString().padStart(2, "0")}:${regularTimeMinutes
        .toString()
        .padStart(
          2,
          "0",
        )}:${regularTimeSecondsRemainder.toString().padStart(2, "0")}`,
    },
  };
};

export const parseTimeFromISO = (
  isoString: string,
): { hour: number; minute: number; ampm: "AM" | "PM" } => {
  const [hour, minute, ampm] = format(isoString, "hh:mm:a").split(":");
  return {
    hour: Number(hour),
    minute: Number(minute),
    ampm: ampm as "AM" | "PM",
  };
};

export const parsePickerTime = (timeStr: string): TimePickerTime => {
  const [hh, mm] = timeStr.split(":").map(Number);

  let ampm: "AM" | "PM" = "AM";
  let hour = hh;

  if (hh === 0) {
    hour = 12;
    ampm = "AM";
  } else if (hh === 12) {
    hour = 12;
    ampm = "PM";
  } else if (hh > 12) {
    hour = hh - 12;
    ampm = "PM";
  } else {
    ampm = "AM";
  }

  return {
    hour,
    minute: mm,
    ampm,
  };
};

export const mbToBytes = (sizeInMB: number) => sizeInMB * 1024 * 1024;
export const buildEmployeeRedirectUrl = (
  baseUrl: string,
  employeeId?: string | number,
) => {
  const searchParams = new URLSearchParams(window.location.search);
  const idParam =
    searchParams.get("id") || (employeeId ? String(employeeId) : undefined);
  const params = new URLSearchParams();
  if (idParam) params.set("id", idParam);
  if (params.toString() !== "") {
    return `${baseUrl}?${params.toString()}`;
  }
  return baseUrl;
};

export const withBranding = (title: string) => `${title} | ${PLATFORM_NAME}`;

export const formatToLocalTime = (isoString?: string | null): string => {
  if (!isoString) return "";

  try {
    const date = parseISO(isoString);
    return format(date, "hh:mm");
  } catch {
    return "";
  }
};

export const convertToUTCDateRange = (startDate?: string, endDate?: string) => {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T23:59:59`);

  // Convert to UTC ISO strings
  const startUTC = new Date(
    Date.UTC(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0),
  ).toISOString();

  const endUTC = new Date(
    Date.UTC(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59),
  ).toISOString();

  return { startUTC, endUTC };
};

export const utcToLocal = (utcString: any) => {
  if (!utcString) return "-- -- --";
  const date = new Date(utcString);
  return isNaN(date.getTime()) ? "-- -- --" : format(date, "hh:mm a");
};

export const formatAsUTC = (date: Date) =>
  dayjs(date).format("YYYY-MM-DDTHH:mm:ss[Z]");

export const combineToUTC = (date: string | Date, time: string) => {
  const dateStr = dayjs(date).format("YYYY-MM-DD");
  const utcTime = dayjs(`1970-01-01T${time}`).utc().format("HH:mm:ss");
  return formatAsUTC(new Date(`${dateStr}T${utcTime}`));
};

export const formatKababToCamelCase = (status: string) => {
  return status
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const isFileAccepted = (file: File, acceptedTypes: string): boolean => {
  const accepted = acceptedTypes.split(",").map((a) => a.trim().toLowerCase());
  const mime = file.type.toLowerCase();
  const ext = "." + file.name.split(".").pop()!.toLowerCase();
  return accepted.some(
    (a) =>
      a === mime ||
      a === ext ||
      (a.endsWith("/*") && mime.startsWith(a.slice(0, -2))),
  );
};
